import { b as __awaiter, _ as _assign, a as __rest, c as __generator } from '../../tslib.es6-c6da0ae6.js';
import fetch from 'isomorphic-fetch';
import { X_APP_AUTHORIZATION, RequestHeaders } from '../../configs/constants/index.js';
import getDeviceId from '../../getDeviceId/index.js';
import IS_BROWSER from '../../isBrowser/index.js';
import errorLogger, { BUGSNAG_STATUSES } from '../errorLogger/index.js';

var _a;
var HttpService = function () {
  function HttpService() {}
  HttpService.safeJsonParse = function (response) {
    return __awaiter(this, void 0, void 0, function () {
      var contentType;
      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            contentType = response.headers.get('content-type');
            if (!(contentType === null || contentType === void 0 ? void 0 : contentType.includes('application/json'))) {
              return [2, null];
            }
            _b.label = 1;
          case 1:
            _b.trys.push([1, 3,, 4]);
            return [4, response.json()];
          case 2:
            return [2, _b.sent()];
          case 3:
            _b.sent();
            return [2, null];
          case 4:
            return [2];
        }
      });
    });
  };
  HttpService.modifiedFetch = function (url, options) {
    var _a;
    if (options === void 0) {
      options = {};
    }
    return __awaiter(this, void 0, void 0, function () {
      var response, responseBody, error, error_1, traceId;
      return __generator(this, function (_b) {
        switch (_b.label) {
          case 0:
            _b.trys.push([0, 3,, 4]);
            return [4, fetch(url, options)];
          case 1:
            response = _b.sent();
            return [4, HttpService.safeJsonParse(response)];
          case 2:
            responseBody = _b.sent();
            if (response.status < 200 || response.status >= 300) {
              error = {
                status: response.status,
                statusText: response.statusText,
                headers: response.headers,
                url: response.url,
                body: responseBody
              };
              throw error;
            }
            return [2, responseBody || {}];
          case 3:
            error_1 = _b.sent();
            if (options.logErrors) {
              traceId = (_a = error_1.headers) === null || _a === void 0 ? void 0 : _a.get('x-trace-id');
              errorLogger.trigger(_assign(_assign({}, error_1), {
                traceId: traceId
              }), BUGSNAG_STATUSES.ERROR);
            }
            throw error_1;
          case 4:
            return [2];
        }
      });
    });
  };
  HttpService.attachInterceptor = function (customFetch, requestUrl, requestOptions) {
    if (requestOptions === void 0) {
      requestOptions = {};
    }
    var skipInterceptors = requestOptions.skipInterceptors;
    if (skipInterceptors || !HttpService.interceptors.length) {
      return customFetch(requestUrl, requestOptions);
    }
    var reversedInterceptors = HttpService.interceptors.reverse();
    var promise = Promise.resolve({
      url: requestUrl,
      options: requestOptions
    });
    reversedInterceptors.forEach(function (_a) {
      var request = _a.request,
        requestError = _a.requestError;
      if (request) {
        promise = promise.then(function (_a) {
          var url = _a.url,
            options = _a.options;
          return request(url, options);
        });
      }
      if (requestError) {
        promise = promise["catch"](requestError);
      }
    });
    promise = promise.then(function (_a) {
      var url = _a.url,
        options = _a.options;
      return customFetch(url, options);
    });
    reversedInterceptors.forEach(function (_a) {
      var response = _a.response,
        responseError = _a.responseError;
      if (response) {
        promise = promise.then(response);
      }
      if (responseError) {
        promise = promise["catch"](responseError);
      }
    });
    return promise;
  };
  HttpService.registerInterceptor = function (interceptor) {
    HttpService.interceptors.push(interceptor);
    return function () {
      HttpService.interceptors = HttpService.interceptors.filter(function (item) {
        return item !== interceptor;
      });
    };
  };
  HttpService.clearInterceptors = function () {
    HttpService.interceptors = [];
  };
  HttpService.setConfig = function (config) {
    HttpService.config = _assign(_assign({}, HttpService.config), config);
    return HttpService;
  };
  HttpService.getFormData = function (data) {
    if (IS_BROWSER && data instanceof FormData) {
      return data;
    }
    return JSON.stringify(data);
  };
  HttpService.getOptions = function (_a) {
    var _b;
    if (_a === void 0) {
      _a = {};
    }
    var withoutLanguageHeader = _a.withoutLanguageHeader,
      logErrors = _a.logErrors,
      options = __rest(_a, ["withoutLanguageHeader", "logErrors"]);
    var reqHeaders = (_b = options.headers) !== null && _b !== void 0 ? _b : {};
    var initialHeaders = _assign({}, HttpService.options.headers);
    if (withoutLanguageHeader) {
      delete initialHeaders['language-code'];
    }
    if (IS_BROWSER && options.body instanceof FormData) {
      delete initialHeaders['Content-Type'];
    }
    var optionsHeaders = _assign(_assign({}, initialHeaders), reqHeaders);
    return _assign(_assign(_assign({}, HttpService.options), options), {
      headers: optionsHeaders,
      logErrors: logErrors
    });
  };
  HttpService.setOptions = function (options) {
    var headers = _assign(_assign({}, options.headers || {}), HttpService.options.headers);
    HttpService.options = _assign(_assign(_assign({}, HttpService.options), options), {
      headers: headers
    });
  };
  HttpService.get = function (url, options) {
    if (options === void 0) {
      options = {};
    }
    var reqOptions = HttpService.getOptions(_assign(_assign({}, options), {
      method: 'GET'
    }));
    return HttpService.fetch(url, reqOptions);
  };
  HttpService.post = function (url, data, options) {
    if (options === void 0) {
      options = {};
    }
    var reqOptions = HttpService.getOptions(_assign(_assign({}, options), {
      method: 'POST',
      body: HttpService.getFormData(data)
    }));
    return HttpService.fetch(url, reqOptions);
  };
  HttpService.put = function (url, data, options) {
    if (options === void 0) {
      options = {};
    }
    var reqOptions = HttpService.getOptions(_assign(_assign({}, options), {
      method: 'PUT',
      body: HttpService.getFormData(data)
    }));
    return HttpService.fetch(url, reqOptions);
  };
  HttpService["delete"] = function (url, data, options) {
    if (options === void 0) {
      options = {};
    }
    var reqOptions = HttpService.getOptions(_assign(_assign({}, options), {
      method: 'DELETE',
      body: HttpService.getFormData(data)
    }));
    return HttpService.fetch(url, reqOptions);
  };
  HttpService.fetch = function (url, options) {
    return HttpService.attachInterceptor(HttpService.modifiedFetch, url, options);
  };
  HttpService.config = {
    apiUrl: ''
  };
  HttpService.options = {
    headers: _assign(_assign({
      'Content-Type': 'application/json'
    }, X_APP_AUTHORIZATION ? (_a = {}, _a[RequestHeaders.xAppAuthorizationToken] = X_APP_AUTHORIZATION, _a) : {}), getDeviceId() && {
      deviceid: getDeviceId()
    })
  };
  HttpService.interceptors = [];
  return HttpService;
}();

export { HttpService as default };
//# sourceMappingURL=index.js.map
